import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Download, AlertCircle, Plus } from "lucide-react";

const ICON_BY_TYPE = {
  id_card: FileText,
  passport: FileText,
  hr_extract: FileText,
  residence_proof: FileText,
  other: FileText,
};

const TYPE_DESCRIPTIONS = {
  id_card: "Vorder- und Rückseite Ihres Personalausweises (PDF/JPG/PNG)",
  passport: "Reisepass — Seite mit Foto und Personendaten",
  hr_extract: "Aktueller Handelsregisterauszug (max. 3 Monate alt)",
  residence_proof: "Meldebescheinigung oder Wohnsitzbestätigung",
  other: "Beliebige weitere Unterlagen (max. 5)",
};

export default function DocumentUploader({ compact = false }) {
  const [types, setTypes] = useState([]);
  const [maxBytes, setMaxBytes] = useState(10485760);
  const [maxOther, setMaxOther] = useState(5);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyType, setBusyType] = useState(null);
  const inputRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const [t, d] = await Promise.all([
        api.get("/intake/documents/types/list"),
        api.get("/intake/documents/me"),
      ]);
      setTypes(t.data.types || []);
      setMaxBytes(t.data.max_bytes || 10485760);
      setMaxOther(t.data.max_other || 5);
      setDocs(d.data || []);
    } catch {
      toast.error("Dokumente konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPick = (docType) => {
    const el = inputRefs.current[docType];
    if (el) el.click();
  };

  const onFile = async (e, docType) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.size > maxBytes) {
      toast.error(`Datei zu gross (${(file.size / 1024 / 1024).toFixed(1)} MB). Max. ${(maxBytes / 1024 / 1024).toFixed(0)} MB.`);
      return;
    }

    let label = "";
    if (docType === "other") {
      label = window.prompt('Bezeichnung für dieses Dokument (z.B. „Mietvertrag"):', file.name);
      if (label === null) return;
      label = label.trim().slice(0, 120);
    }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    if (label) fd.append("label", label);

    setBusyType(docType);
    try {
      await api.post("/intake/documents/upload", fd);
      toast.success("Dokument hochgeladen");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusyType(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    try {
      await api.delete(`/intake/documents/${id}`);
      toast.success("Gelöscht");
      await load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const onDownload = async (id, filename) => {
    try {
      const res = await api.get(`/intake/documents/${id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };

  const docsForType = (key) => docs.filter((d) => d.doc_type === key);

  if (loading) return <div className="text-slate-500 text-sm">Lade Dokumente…</div>;

  return (
    <div data-testid="document-uploader" className="space-y-3">
      {!compact && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
          <div className="text-amber-900">
            Alle Dokumente sind <strong>optional</strong>. Erlaubt: PDF, JPG, PNG, DOCX, XLSX — max. {(maxBytes / 1024 / 1024).toFixed(0)} MB.
            Ihre Daten werden verschlüsselt übertragen und nur unserem Team zugänglich gemacht.
          </div>
        </div>
      )}

      {types.map((t) => {
        const list = docsForType(t.key);
        const isOther = t.key === "other";
        const limitReached = isOther && list.length >= maxOther;
        const Icon = ICON_BY_TYPE[t.key] || FileText;
        return (
          <div key={t.key} className="border border-slate-200 rounded-xl p-4 bg-white" data-testid={`doc-section-${t.key}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-[#1A2433] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#DC2626]" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[#1A2433] text-sm">
                    {t.label}
                    {isOther && (
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        ({list.length}/{maxOther})
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{TYPE_DESCRIPTIONS[t.key]}</div>
                </div>
              </div>
              <button
                onClick={() => onPick(t.key)}
                disabled={busyType === t.key || limitReached}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#991B1B] text-white text-xs font-medium rounded-lg hover:bg-[#7c1414] disabled:opacity-40 transition-colors"
                data-testid={`doc-upload-btn-${t.key}`}
              >
                {isOther ? <Plus className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
                {busyType === t.key ? "Lädt…" : (list.length > 0 && !isOther ? "Ersetzen" : "Hochladen")}
              </button>
              <input
                ref={(el) => (inputRefs.current[t.key] = el)}
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.doc,.xls,application/pdf,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => onFile(e, t.key)}
                data-testid={`doc-input-${t.key}`}
              />
            </div>

            {list.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {list.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                    data-testid={`doc-item-${d.id}`}
                  >
                    <div className="min-w-0 flex items-center gap-2 flex-1">
                      <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="truncate text-[#1A2433] font-medium">{d.label || d.filename}</div>
                        <div className="text-[11px] text-slate-500">
                          {d.filename} · {(d.size / 1024).toFixed(0)} KB · {new Date(d.created_at).toLocaleDateString("de-CH")}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => onDownload(d.id, d.filename)}
                        className="p-1.5 text-slate-500 hover:text-[#991B1B] hover:bg-white rounded transition-colors"
                        title="Herunterladen"
                        data-testid={`doc-download-${d.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onDelete(d.id)}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-white rounded transition-colors"
                        title="Löschen"
                        data-testid={`doc-delete-${d.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
